import type { TFunction } from 'i18next';

import { categoriaQueSube, claveFraseGavi, diasEntre } from './diario';
import type { Periodo, ResumenDiario } from './diario';
import type { TipoDiario } from './useResumenDiario';

/** «12 – 18 de octubre», «28 de septiembre – 4 de octubre». */
export function nombrePeriodo(
  t: TFunction,
  p: Periodo,
  idioma: string,
): string {
  const fecha = (f: string, opciones: Intl.DateTimeFormatOptions) =>
    new Date(`${f}T12:00:00`).toLocaleDateString(idioma, opciones);
  const mismoMes = p.desde.slice(0, 7) === p.hasta.slice(0, 7);
  return t('{{from}} – {{to}}', {
    from: mismoMes
      ? fecha(p.desde, { day: 'numeric' })
      : fecha(p.desde, { day: 'numeric', month: 'long' }),
    to: fecha(p.hasta, { day: 'numeric', month: 'long' }),
  });
}

/** «25 sep – 24 oct» (periodo de nómina a nómina, corto). */
export function nombrePeriodoCorto(
  t: TFunction,
  p: Periodo,
  idioma: string,
): string {
  const fecha = (f: string) =>
    new Date(`${f}T12:00:00`).toLocaleDateString(idioma, {
      day: 'numeric',
      month: 'short',
    });
  return t('{{from}} – {{to}}', { from: fecha(p.desde), to: fecha(p.hasta) });
}

export function mayuscula(texto: string): string {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

/**
 * Lo que dice Gavi del periodo: una frase sobre el total y, si una
 * categoría se disparó, otra que la nombra.
 */
export function fraseGavi({
  t,
  resumen: r,
  tipo,
  fmt,
  nombreCategoria,
  nombreMesMejor,
}: {
  t: TFunction;
  resumen: ResumenDiario;
  tipo: TipoDiario;
  fmt: (centimos: number) => string;
  nombreCategoria: (id: string | null) => string;
  /** Nombre del último mes que fue mejor («junio»), para «mejor desde». */
  nombreMesMejor: string | null;
}): string {
  const clave = claveFraseGavi(r, tipo);
  const semana = tipo === 'semana';
  const ref =
    r.gastoHabitual == null
      ? 0
      : r.enCurso && semana
        ? Math.round((r.gastoHabitual * r.diasTranscurridos) / 7)
        : r.gastoHabitual;
  const diferencia = Math.abs(r.gastos - ref);
  const sube = categoriaQueSube(r);

  let frase: string;
  switch (clave) {
    case 'sin-datos':
      return semana
        ? t('Nothing jotted down this week yet. Calm seas or a forgotten log?')
        : t('Nothing jotted down this month yet.');
    case 'mejor-de-todos':
      frase = t('The month you kept the most since we set sail together.');
      break;
    case 'mejor-desde':
      frase = t('Your best month since {{month}}.', {
        month: nombreMesMejor ?? '',
      });
      break;
    case 'en-rojo':
      frase = t('More went out than came in. Time to check the course.');
      break;
    case 'por-debajo':
      frase = semana
        ? r.enCurso
          ? t('{{amount}} less than a normal week so far. Fair winds.', {
              amount: fmt(diferencia),
            })
          : t('{{amount}} less than a normal week. Fair winds.', {
              amount: fmt(diferencia),
            })
        : t('{{amount}} less spending than a normal month.', {
            amount: fmt(diferencia),
          });
      break;
    case 'por-encima':
      return sube && sube.categoria != null
        ? t('{{amount}} more than usual, mostly in {{category}}.', {
            amount: fmt(diferencia),
            category: nombreCategoria(sube.categoria),
          })
        : t('{{amount}} more than usual. Hold the helm steady.', {
            amount: fmt(diferencia),
          });
    default:
      frase = semana
        ? t('A week like any other. Steady course.')
        : t('A month like any other. Steady course.');
  }
  if (sube && sube.categoria != null && sube.diferencia != null) {
    return `${frase} ${t(
      'Watch out for {{category}}: {{amount}} more than usual.',
      {
        category: nombreCategoria(sube.categoria),
        amount: fmt(sube.diferencia),
      },
    )}`;
  }
  if (semana && r.diasSinGastar >= 2 && !r.enCurso) {
    return `${frase} ${t('{{count}} days with your wallet closed.', {
      count: r.diasSinGastar,
    })}`;
  }
  return frase;
}

/** Días del periodo (para «9 de 30»). */
export function diasDelPeriodo(p: Periodo): number {
  return diasEntre(p.desde, p.hasta) + 1;
}
